import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Renders course modules as an accordion.
 * `modules` = [{ n, title, sessions, points: [string] }]
 */
export default function ModulesAccordion({
  modules = [],
  accent = "terracotta",
  testid = "modules-accordion",
}) {
  const reduced = useReducedMotion();
  const isTerra = accent === "terracotta";
  const bgPill = isTerra ? "rgba(200,106,74,0.12)" : "rgba(95,127,107,0.18)";
  const fgPill = isTerra ? "#C86A4A" : "#3f5a4d";
  const pillTextBg = isTerra ? "rgba(242,215,182,0.45)" : "rgba(126,155,134,0.22)";
  const pillTextFg = isTerra ? "#8a4128" : "#3f5a4d";

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 8 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.45 }}
      data-testid={testid}
      className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-2 sm:p-3"
    >
      <Accordion type="single" collapsible className="w-full">
        {modules.map((m) => (
          <AccordionItem
            key={m.n}
            value={`module-${m.n}`}
            className="border-b last:border-b-0 border-[rgba(74,63,57,0.08)]"
          >
            <AccordionTrigger
              data-testid={`${testid}-trigger-${m.n}`}
              className="py-5 px-3 sm:px-4 hover:no-underline focus:no-underline group [&>svg]:text-ink-muted"
            >
              <div className="flex items-start gap-4 text-left flex-1">
                <div
                  className="shrink-0 w-10 h-10 rounded-xl font-display text-base flex items-center justify-center"
                  style={{ backgroundColor: bgPill, color: fgPill }}
                >
                  {String(m.n).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="font-display text-base sm:text-lg text-ink leading-snug group-hover:text-terracotta transition-colors">
                      Module {m.n}: {m.title}
                    </h3>
                  </div>
                  <div className="mt-1.5">
                    <span
                      className="inline-block text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: pillTextBg, color: pillTextFg }}
                    >
                      {m.sessions} {m.sessions === 1 ? "session" : "sessions"}
                    </span>
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent
              data-testid={`${testid}-content-${m.n}`}
              className="px-3 sm:px-4 pb-5"
            >
              <ul className="pl-14 sm:pl-16 space-y-2">
                {m.points.map((p) => (
                  <li
                    key={p}
                    className="flex gap-3 text-ink leading-[1.7] text-[14.5px] sm:text-[15px]"
                  >
                    <span
                      className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: fgPill }}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </motion.div>
  );
}
